import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CheckCircle2, Sparkles, Target, Users } from 'lucide-react';
import Reveal from '../components/Reveal';
import SectionHeader from '../components/SectionHeader';
import { TALENT_ROLES, ENGAGEMENT_MODELS, PROCESS, INDUSTRIES, IMAGES } from '../mock/data';

const Talent = () => (
  <>
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="hero-glow" />
      <div className="container-x relative grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-300 text-xs font-medium uppercase tracking-widest mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Talent Solutions
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-display text-5xl md:text-6xl font-bold text-white leading-[1.05]">
              IT staffing that puts the <span className="shimmer-text">right engineer</span> on your team.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-white/60 text-lg mt-6">
              A specialist IT staffing service for US enterprises that need vetted engineering talent — without a months-long search. Contract, contract-to-hire and direct-hire on your terms.
            </p>
          </Reveal>
          <Reveal delay={220}>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/contact" className="btn-primary inline-flex items-center gap-1.5 px-6 py-3.5 rounded-full text-sm">
                Start a conversation <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/careers" className="btn-outline inline-flex items-center gap-1.5 px-6 py-3.5 rounded-full text-sm">
                Explore open roles
              </Link>
            </div>
          </Reveal>
        </div>
        <Reveal delay={200}>
          <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/3]">
            <img src={IMAGES.hiringB} alt="Talent" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#05070E] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 glass rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-400/20 flex items-center justify-center"><Users className="w-5 h-5 text-cyan-300" /></div>
                <div>
                  <div className="text-xs text-cyan-300 font-medium uppercase tracking-widest">Nationwide network</div>
                  <div className="text-sm text-white font-medium">Pre-vetted senior engineers, ready to interview</div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="section">
      <div className="container-x">
        <div className="grid lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="glass rounded-2xl p-8 h-full">
              <div className="text-xs uppercase tracking-widest text-cyan-300/80 mb-3">What's included</div>
              <h3 className="font-display text-2xl font-semibold text-white mb-5">Everything you need to hire fast</h3>
              <ul className="space-y-3">
                {[
                  'Senior AI, ML, data and MLOps engineers, pre-vetted for your stack',
                  'Contract, contract-to-hire and direct-hire placement',
                  'Technical screening and reference checks before you interview',
                  'Onboarding support and ongoing consultant care',
                  'Compliance, payroll and W-2 employment handled for you',
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="glass rounded-2xl p-8 h-full">
              <div className="text-xs uppercase tracking-widest text-cyan-300/80 mb-3">Who it's for</div>
              <h3 className="font-display text-2xl font-semibold text-white mb-5">Engineering & talent leaders</h3>
              <p className="text-white/70 leading-relaxed">
                US technology companies who need to fill a critical role quickly, cover a short-term vacancy, or hire specialised AI skills that are hard to source through traditional channels.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {['Series B–D scale-ups', 'Enterprise engineering', 'AI-first product teams', 'Regulated industries'].map((t, i) => (
                  <span key={i} className="px-3 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-white/80">{t}</span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container-x">
        <SectionHeader eyebrow="Roles we source" title="Specialised talent across" accent="the modern AI & data stack." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {TALENT_ROLES.map((r, i) => (
            <Reveal key={i} delay={i * 50}>
              <div className="tilt-card glass rounded-xl p-5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-cyan-400/10 border border-cyan-400/20 text-cyan-300 flex items-center justify-center shrink-0">
                  <Target className="w-4 h-4" />
                </div>
                <div className="text-sm text-white/90 font-medium">{r}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container-x">
        <SectionHeader eyebrow="Engagement models" title="Flexible ways to" accent="work with us." />
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {ENGAGEMENT_MODELS.map((m, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="tilt-card glass rounded-2xl p-7 h-full">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/20 text-cyan-300 text-[10px] uppercase tracking-widest font-medium mb-4">{m.tag}</div>
                <h3 className="font-display text-xl font-semibold text-white mb-2">{m.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed mb-5">{m.desc}</p>
                <ul className="space-y-2">
                  {m.perks.map((p, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-white/80"><CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" /> {p}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="section relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="container-x relative">
        <SectionHeader align="center" eyebrow="How it works" title="A precise 4-step" accent="hiring process." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {PROCESS.map((p, i) => (
            <Reveal key={i} delay={i * 120}>
              <div className="tilt-card glass rounded-2xl p-7 h-full relative">
                <div className="font-display text-6xl font-bold text-white/5 absolute top-4 right-5">{p.step}</div>
                <div className="w-10 h-10 rounded-lg bg-cyan-400 text-[#05070E] font-bold flex items-center justify-center mb-5">{p.step}</div>
                <h3 className="font-display text-lg font-semibold text-white mb-2">{p.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="text-center mt-14">
          <Link to="/contact" className="btn-primary inline-flex items-center gap-1.5 px-6 py-3.5 rounded-full text-sm">
            Ready to fill a role? <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default Talent;
